import { translate } from "@blocksuite/affine-shared/services";
import { SignalWatcher } from "@blocksuite/global/lit";
import type { BlockComponent } from "@blocksuite/std";
import { css, html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import { MermaidIcon } from "../icons.js";
import { MERMAID_TEMPLATES, type MermaidTemplateId } from "../templates.js";

export class EdgelessMermaidMenu extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 1;
      display: block;
    }
    .panel {
      width: 288px;
      max-height: min(320px, 55vh);
      overflow: auto;
      box-sizing: border-box;
      padding: 8px;
      border: 1px solid var(--affine-border-color);
      border-radius: 10px;
      background: var(--affine-background-overlay-panel-color);
      box-shadow: var(--affine-shadow-2);
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px;
    }
    button {
      appearance: none;
      border: 0;
      background: transparent;
      color: var(--affine-text-primary-color);
      min-width: 0;
      padding: 8px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      gap: 8px;
      text-align: left;
      cursor: pointer;
      font: inherit;
    }
    button:hover,
    button[data-selected] {
      background: var(--affine-hover-color);
    }
    .icon {
      width: 20px;
      height: 20px;
      flex: 0 0 auto;
      display: grid;
      place-items: center;
    }
    .icon svg {
      width: 20px;
      height: 20px;
    }
    .label {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: var(--affine-font-xs);
    }
  `;

  override firstUpdated() {
    const selected =
      this.renderRoot.querySelector<HTMLButtonElement>(
        "button[data-selected]",
      ) ?? this.renderRoot.querySelector<HTMLButtonElement>("button");
    selected?.focus();
  }

  private _onKeyDown(event: KeyboardEvent) {
    const buttons = [
      ...this.renderRoot.querySelectorAll<HTMLButtonElement>("button"),
    ];
    const current = buttons.indexOf(event.target as HTMLButtonElement);
    if (event.key === "Escape") {
      event.preventDefault();
      this.dispatchEvent(
        new CustomEvent("mermaid-menu-close", {
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    const keys = [
      "ArrowDown",
      "ArrowRight",
      "ArrowUp",
      "ArrowLeft",
      "Home",
      "End",
    ];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    let next = current < 0 ? 0 : current;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = buttons.length - 1;
    else if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      next = (next + 1) % buttons.length;
    } else {
      next = (next - 1 + buttons.length) % buttons.length;
    }
    buttons[next]?.focus();
  }

  override render() {
    return html`
      <div
        class="panel"
        role="menu"
        aria-label=${translate(this.edgeless.std, "Mermaid template menu") ??
        "Mermaid template menu"}
        @keydown=${this._onKeyDown}
      >
        ${repeat(
          MERMAID_TEMPLATES,
          (template) => template.id,
          (template) => html`
            <button
              role="menuitem"
              aria-current=${template.id === this.selected ? "true" : "false"}
              ?data-selected=${template.id === this.selected}
              @click=${() => this.onChange(template.id)}
            >
              <span class="icon">${MermaidIcon}</span>
              <span class="label"
                >${translate(this.edgeless.std, template.name) ??
                template.name}</span
              >
            </button>
          `,
        )}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor edgeless!: BlockComponent;

  @property({ attribute: false })
  accessor onChange: (template: MermaidTemplateId) => void = () => {};

  @property({ attribute: false })
  accessor selected: MermaidTemplateId = "flowchart";
}
